import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CalendarAudioRecorder from './CalendarAudioRecorder';
import '../styles/calendar-entry-dialog.css';

const CalendarEntryDialog = ({ date, entry, onSave, onCancel }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // Form state
  const [formData, setFormData] = useState({
    entry_type: 'rehearsal_notes',
    title: '',
    content: '',
    content_html: '',
    venue_name: '',
    venue_address: '',
    venue_maps_url: '',
    performance_time: '',
    icon: '',
    audio_clip_ids: []
  });
  
  const [availableAudioClips, setAvailableAudioClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachedAudioClips, setAttachedAudioClips] = useState([]);
  
  // Load initial data
  useEffect(() => {
    if (entry) {
      setFormData({
        entry_type: entry.entry_type || 'rehearsal_notes',
        title: entry.title || '',
        content: entry.content || '',
        content_html: entry.content_html || '',
        venue_name: entry.venue_name || '',
        venue_address: entry.venue_address || '',
        venue_maps_url: entry.venue_maps_url || '',
        performance_time: entry.performance_time || '',
        icon: entry.icon || getDefaultIcon(entry.entry_type || 'rehearsal_notes'),
        audio_clip_ids: entry.audio_clips?.map(clip => clip.id) || []
      });
    } else {
      // Set default icon for new entries
      setFormData(prev => ({
        ...prev,
        icon: prev.icon || getDefaultIcon(prev.entry_type)
      }));
    }
    
    loadAudioClips();
  }, [entry]);
  
  // Get default icon for entry type
  const getDefaultIcon = (entryType) => {
    switch (entryType) {
      case 'rehearsal_notes': return '📝';
      case 'performance': return '🎭';
      case 'notice': return '📢';
      default: return '📅';
    }
  };
  
  // Load available audio clips
  const loadAudioClips = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/audio-clips?operation=getAudioClips&userId=${user.id}&v=${Date.now()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        setAvailableAudioClips(result || []);
      } else {
        console.log('Audio clips API not available or no permission');
        setAvailableAudioClips([]);
      }
    } catch (error) {
      console.error('Failed to load audio clips:', error);
      setAvailableAudioClips([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = entry 
        ? `/api/calendar?operation=updateEntry&id=${entry.id}`
        : '/api/calendar?operation=createEntry';
      
      const method = 'POST'; // Use POST for both create and update
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          // Don't send icon if database doesn't have the column yet
          ...(formData.icon ? { icon: formData.icon } : {}),
          date,
          userId: user.id,
          userRole: user.role
        })
      });
      
      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        console.error('Server error response:', error);
        throw new Error(error.details || error.error || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Failed to save calendar entry:', error);
      alert(`Failed to save entry: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle field changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-update icon when entry type changes (only if no custom icon set)
      if (field === 'entry_type' && (!prev.icon || prev.icon === getDefaultIcon(prev.entry_type))) {
        newData.icon = getDefaultIcon(value);
      }
      
      return newData;
    });
  };
  
  // Handle rich text editor change
  const handleContentChange = (content, delta, source, editor) => {
    setFormData(prev => ({
      ...prev,
      content: editor.getText(),
      content_html: content
    }));
  };
  
  // Handle audio clip selection
  const handleAudioClipToggle = (clipId) => {
    setFormData(prev => ({
      ...prev,
      audio_clip_ids: prev.audio_clip_ids.includes(clipId)
        ? prev.audio_clip_ids.filter(id => id !== clipId)
        : [...prev.audio_clip_ids, clipId]
    }));
  };
  
  // Handle new audio recording/upload
  const handleAudioAdded = (newClip) => {
    setAttachedAudioClips(prev => [...prev, newClip]);
    setFormData(prev => ({
      ...prev,
      audio_clip_ids: [...prev.audio_clip_ids, newClip.id]
    }));
  };
  
  // Quill modules configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
  };
  
  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block', 'link'
  ];
  
  const entryTypes = [
    { value: 'rehearsal_notes', label: 'Rehearsal Notes' },
    { value: 'performance', label: 'Performance' },
    { value: 'notice', label: 'General Notice' }
  ];
  
  return (
    <div className="dialog-overlay" onClick={onCancel}>
      <div className="calendar-entry-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>
            {entry ? 'Edit Calendar Entry' : 'Add Calendar Entry'}
          </h3>
          <button className="close-button" onClick={onCancel} disabled={saving}>
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="entry-form">
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={date}
                  disabled
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Entry Type</label>
                <select
                  value={formData.entry_type}
                  onChange={(e) => handleChange('entry_type', e.target.value)}
                  className="form-select"
                  disabled={saving}
                >
                  {entryTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Icon</label>
                <div className="icon-picker">
                  <div className="icon-preview">
                    <span className="selected-icon">{formData.icon || '📅'}</span>
                  </div>
                  <select
                    value={formData.icon}
                    onChange={(e) => handleChange('icon', e.target.value)}
                    className="form-select"
                    disabled={saving}
                  >
                    <option value="📅">📅 Default</option>
                    <option value="📝">📝 Notes</option>
                    <option value="🎭">🎭 Performance</option>
                    <option value="📢">📢 Notice</option>
                    <option value="🎵">🎵 Music</option>
                    <option value="🎤">🎤 Singing</option>
                    <option value="🎹">🎹 Piano</option>
                    <option value="🎺">🎺 Brass</option>
                    <option value="🎻">🎻 Strings</option>
                    <option value="🥁">🥁 Percussion</option>
                    <option value="🎸">🎸 Guitar</option>
                    <option value="🎶">🎶 Musical Note</option>
                    <option value="🗓️">🗓️ Calendar</option>
                    <option value="⏰">⏰ Time</option>
                    <option value="📍">📍 Location</option>
                    <option value="🎯">🎯 Goal</option>
                    <option value="⭐">⭐ Important</option>
                    <option value="🔥">🔥 Hot</option>
                    <option value="💡">💡 Idea</option>
                    <option value="✅">✅ Complete</option>
                    <option value="❗">❗ Alert</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter title..."
                className="form-input"
                disabled={saving}
                required
              />
            </div>
          </div>
          
          {/* Performance-specific fields */}
          {formData.entry_type === 'performance' && (
            <div className="form-section">
              <h4>Performance Details</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Venue</label>
                  <input
                    type="text"
                    value={formData.venue_name}
                    onChange={(e) => handleChange('venue_name', e.target.value)}
                    placeholder="Venue name..."
                    className="form-input"
                    disabled={saving}
                  />
                </div>
                
                <div className="form-group">
                  <label>Performance Time</label>
                  <input
                    type="time"
                    value={formData.performance_time}
                    onChange={(e) => handleChange('performance_time', e.target.value)}
                    className="form-input"
                    disabled={saving}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Venue Address</label>
                <textarea
                  value={formData.venue_address}
                  onChange={(e) => handleChange('venue_address', e.target.value)}
                  placeholder="Venue address..."
                  className="form-textarea"
                  rows="2"
                  disabled={saving}
                />
              </div>
              
              <div className="form-group">
                <label>Maps URL</label>
                <input
                  type="url"
                  value={formData.venue_maps_url}
                  onChange={(e) => handleChange('venue_maps_url', e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="form-input"
                  disabled={saving}
                />
              </div>
            </div>
          )}
          
          {/* Content editor */}
          <div className="form-section">
            <label>Content</label>
            <div className="quill-wrapper">
              <ReactQuill
                value={formData.content_html}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder={
                  formData.entry_type === 'rehearsal_notes' 
                    ? 'What happened during this rehearsal?'
                    : 'Enter content...'
                }
                readOnly={saving}
                theme="snow"
              />
            </div>
          </div>
          
          {/* Audio clips */}
          <div className="form-section">
            <label>Audio Clips</label>
            <CalendarAudioRecorder
              onAudioAdded={handleAudioAdded}
              selectedAudioIds={formData.audio_clip_ids}
              onSelectionChange={handleAudioClipToggle}
            />
            
            {/* Show currently attached clips */}
            {formData.audio_clip_ids.length > 0 && (
              <div className="attached-clips">
                <h5>Attached Clips ({formData.audio_clip_ids.length})</h5>
                <div className="clips-preview">
                  {[...availableAudioClips, ...attachedAudioClips]
                    .filter(clip => formData.audio_clip_ids.includes(clip.id))
                    .map(clip => (
                      <div key={clip.id} className="attached-clip">
                        <span>{clip.name || clip.title}</span>
                        <button
                          type="button"
                          onClick={() => handleAudioClipToggle(clip.id)}
                          className="remove-clip"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="dialog-actions">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
              disabled={saving}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="save-button"
              disabled={saving || !formData.title.trim()}
            >
              {saving 
                ? 'Saving...'
                : (entry ? 'Update Entry' : 'Create Entry')
              }
            </button>
          </div>
        </form>
        
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarEntryDialog;